import React, { useState } from 'react';
import { Trash2, BarChart3 } from '../../icons';
import SyncHistoryCard from './SyncHistoryCard';
import ErrorDisplay from '../common/ErrorDisplay';
import Chip, { ChipRow } from '../ui/Chip';
import Menu from '../ui/Menu';
import Sheet from '../ui/Sheet';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';

const PERIODS = [
  { value: 'all', label: 'All time' },
  { value: '24h', label: 'Last 24h' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

const SyncHistoryContainer = ({
  syncHistory,
  isLoading,
  error,
  onDeleteSync,
  onClearOldHistory,
  addToast
}) => {
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [isClearing, setIsClearing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Filter sync history by time period
  const getFilteredHistory = () => {
    if (filterPeriod === 'all') return syncHistory;
    
    const now = new Date();
    const cutoffDate = new Date();
    
    switch (filterPeriod) {
      case '24h':
        cutoffDate.setHours(now.getHours() - 24);
        break;
      case '7d':
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case '30d':
        cutoffDate.setDate(now.getDate() - 30);
        break;
      default:
        return syncHistory;
    }
    
    return syncHistory.filter(sync => sync.timestamp >= cutoffDate);
  };

  const filteredHistory = getFilteredHistory();

  const handleClearOldHistory = async () => {
    setShowClearConfirm(false);
    setIsClearing(true);
    try {
      const deletedCount = await onClearOldHistory(90);
      if (addToast) {
        addToast(`✅ Cleared ${deletedCount} old sync records`, 'success');
      }
    } catch (error) {
      if (addToast) {
        addToast(`❌ Failed to clear history: ${error.message}`, 'error');
      }
    } finally {
      setIsClearing(false);
    }
  };

  const handleDeleteSync = async (syncId) => {
    try {
      await onDeleteSync(syncId);
      if (addToast) {
        addToast('✅ Sync record deleted', 'success');
      }
    } catch (error) {
      if (addToast) {
        addToast(`❌ Failed to delete: ${error.message}`, 'error');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Loading sync history">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-24 bg-card border border-line rounded-card animate-pulse" />
        ))}
      </div>
    );
  }

  const totalUpdates = filteredHistory.reduce((sum, sync) => sum + (sync.totalUpdated || 0), 0);
  const totalErrors = filteredHistory.reduce((sum, sync) => sum + (sync.totalErrors || 0), 0);
  const durations = filteredHistory.map(sync => parseFloat(sync.duration)).filter(d => !isNaN(d));
  const avgDuration = durations.length > 0 ? (durations.reduce((sum, d) => sum + d, 0) / durations.length).toFixed(1) : '0';

  return (
    <div className="space-y-4">
      {/* Filters + actions */}
      <div className="flex items-center justify-between gap-3">
        <ChipRow className="flex-1">
          {PERIODS.map((p) => (
            <Chip key={p.value} active={filterPeriod === p.value} onClick={() => setFilterPeriod(p.value)}>
              {p.label}
            </Chip>
          ))}
        </ChipRow>
        <Menu
          label="Sync history actions"
          items={[
            { label: isClearing ? 'Clearing…' : 'Clear records older than 90 days', icon: Trash2, danger: true, disabled: isClearing, onSelect: () => setShowClearConfirm(true) },
          ]}
        />
      </div>

      {/* Error Display */}
      <ErrorDisplay error={error} />

      {/* Stats Summary */}
      {filteredHistory.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          {[
            { label: 'Syncs', value: filteredHistory.length },
            { label: 'Updates', value: totalUpdates, accent: true },
            { label: 'Errors', value: totalErrors, danger: totalErrors > 0 },
            { label: 'Avg duration', value: `${avgDuration}s` },
          ].map((s) => (
            <div key={s.label} className="bg-card border border-line rounded-card px-3.5 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">{s.label}</div>
              <div className={`mt-0.5 text-[22px] font-bold tabular leading-tight ${s.accent ? 'text-accent' : s.danger ? 'text-danger' : 'text-primary'}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Sync History List */}
      {filteredHistory.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title="No sync history"
          description={
            filterPeriod === 'all'
              ? 'Sync history will appear here after your first property sync.'
              : 'No syncs found for the selected time period.'
          }
        />
      ) : (
        <ol className="relative space-y-3 pl-5 before:absolute before:left-[7px] before:top-3 before:bottom-3 before:w-px before:bg-line">
          {filteredHistory.map((sync) => (
            <li key={sync.id} className="relative">
              <span className={`absolute -left-5 top-5 w-[9px] h-[9px] rounded-full border-2 border-page ${sync.totalErrors > 0 ? 'bg-danger' : sync.totalUpdated > 0 ? 'bg-accent' : 'bg-line-strong'}`} />
              <SyncHistoryCard sync={sync} onDelete={() => handleDeleteSync(sync.id)} />
            </li>
          ))}
        </ol>
      )}

      <Sheet isOpen={showClearConfirm} onClose={() => setShowClearConfirm(false)} mode="dialog" size="sm" title="Clear old sync history">
        <p className="text-sm text-primary">Clear sync history older than 90 days?</p>
        <p className="text-xs text-secondary mt-2">This cannot be undone.</p>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="secondary" onClick={() => setShowClearConfirm(false)}>Cancel</Button>
          <Button variant="danger-solid" onClick={handleClearOldHistory}>
            <Trash2 className="w-4 h-4" /> Clear
          </Button>
        </div>
      </Sheet>
    </div>
  );
};

export default SyncHistoryContainer;
